import Util from '../../global/util';
import { Inventory } from '../opn-snapshot/opn-snapshot.interface';
import { ExchangeRate, ParentToolType, ToolDisplayType, ToolMatch } from './tool-snapshot.interface';

export default class ToolDao {
    public static tiHost: string = '';
    public static exchangeRateApiUrl: string = '/search/exchangeRates';
    public static searchApiUrl: string = '/search/tool';
    public static inventoryApiUrl: string = '/productmodel/opn/{opn}/tistoresegmented';

    public static async searchToolMatch(searchTerm: string, locale: string, exchangeRateApiUrl?: string, searchApiUrl?: string, tiHost?: string): Promise<ToolMatch> {
        ToolDao.tiHost = tiHost ? tiHost : '';
        ToolDao.exchangeRateApiUrl = exchangeRateApiUrl ? exchangeRateApiUrl : ToolDao.exchangeRateApiUrl;
        ToolDao.searchApiUrl = searchApiUrl ? searchApiUrl : ToolDao.searchApiUrl;
        const match: ToolMatch = await this.fetchToolMatch(searchTerm, locale);
        return match;
    }

    public static async fetchToolMatch(searchTerm: string, locale: string): Promise<ToolMatch> {
        searchTerm = searchTerm.trim().toUpperCase();
        const url = `${this.tiHost}${this.searchApiUrl}?searchTerm=${searchTerm}&locale=${locale}`;
        const response = await fetch(url);
        if (!response.ok) {
            return null;
        }
        const match: ToolMatch = await response.json();
        if (!match || !match.toolPartNumber) {
            return null;
        }

        // check tool folder exists
        if (!await this.checkValidToolFolderUrl(this.tiHost, match.toolFolderUrl)) {
            return null;
        }

        // tool search
        if (match.isPartnerResource) {
            match.toolGroup = '3p';
        } else {
            switch (match.parentToolTypeId) {
                case ParentToolType.HARDWARE_DEVELOPMENT:
                    match.toolGroup = 'hardware';
                    break;
                case ParentToolType.SOFTWARE_DEVELOPMENT:
                case ParentToolType.DESIGN_TOOLS:
                    match.toolGroup = 'software';
                    break;
                case ParentToolType.REFERENCE_DESIGNS:
                    match.toolGroup = 'refdesign';
                    break;
                default:
                    match.toolGroup = 'tool';
                    break;
            }
        }
        match.displayType = ToolDisplayType.ViewDetails; // default for SW and Reference Design
        match.displayType = match.multipleBoards ? ToolDisplayType.MultipleBoards : match.displayType;
        if (match.orderingInfo && match.orderingInfo.orderButtonId) {
            if (match.orderingInfo.orderButtonId == 7) { // Add to Cart
                // check login status
                if (!match.orderingInfo.available) {
                    match.displayType = ToolDisplayType.NotAvailable;
                } else {
                    const inventory = await ToolDao.fetchToolInventory(searchTerm);
                    match.inventory = inventory;
                    match.displayType = !inventory
                        ? ToolDisplayType.LoggedOut
                        : !inventory.inventory
                            ? ToolDisplayType.OutOfStock
                            : ToolDisplayType.AddToCart;
                }
            } else if (match.orderingInfo.orderButtonId == 6) { // Request
                match.displayType = ToolDisplayType.Request;
            }
            // currency exchange rates
            if (match.displayType == ToolDisplayType.Request || match.displayType == ToolDisplayType.AddToCart || match.displayType == ToolDisplayType.LoggedOut) {
                const currencyExchangeRate = await ToolDao.fetchExchangeRates();
                match.orderingInfo.currencyExchangeRate = {}
                currencyExchangeRate.forEach(exchangeRage => {
                    match.orderingInfo.currencyExchangeRate[exchangeRage.currency] = exchangeRage.exchangeRate;
                });
            }
        }
        return match;
    }

    public static async fetchToolInventory(searchTerm: string): Promise<Inventory> {
        const url = Util.formatUrl(`${this.tiHost}${this.inventoryApiUrl}`, { opn: searchTerm });
        return fetch(url).then(response => {
            if (!response.ok) {
                return null;
            }
            return response.json();
        }).catch(() => {
            return { orderLimit: 999999 } as Inventory;
        });
    }

    public static async fetchExchangeRates(): Promise<ExchangeRate[]> {
        const url = `${this.tiHost}${this.exchangeRateApiUrl}`;
        const response = await fetch(url);
        if (!response.ok) {
            return null;
        }
        const json = await response.json();
        return json as ExchangeRate[];
    }

    public static async checkValidToolFolderUrl(tiHost: string, toolFolderUrl: string): Promise<Boolean> {
        const url = `${tiHost}${toolFolderUrl}`;
        return fetch(url, {
            method: 'HEAD'
        }).then(response => {
            return response.ok && !response.redirected;
        }).catch(() => {
            return false;
        });
    }
}
